import type { PageContextScreen } from "../../lib/page-context";
import { readWebStorage, writeWebStorage } from "../../lib/web-storage";
import { normalizeRoutePath } from "../../lib/chat-navigation";

export type RouteIntroChip = {
  id: string;
  titleKey: string;
  /** Sent verbatim to the agent when the chip is tapped. */
  prompt: string;
};

export type RouteIntro = {
  /**
   * Interchangeable phrasings of the same "here is what I can do on this
   * screen" message. One is picked per visit; see pickIntroVariantIndex.
   */
  messageKeys: string[];
  chips: RouteIntroChip[];
};

/**
 * Static, deterministic per-screen intros. They are deliberately not
 * LLM-generated.
 *
 * Generating these at runtime would pay tokens for chrome copy, add latency to
 * every page landing, and leave Arabic unreviewed. Variants are authored here
 * and localized in `seeker.ts` (en + ar); a dev-time script can append more
 * later without any runtime change.
 *
 * Same shape as `follow-up-suggestions.ts`, keyed by screen instead of tool.
 */
const ROUTE_INTROS: Partial<Record<PageContextScreen, RouteIntro>> = {
  cv: {
    messageKeys: [
      "chat.routeIntro.cv.m1",
      "chat.routeIntro.cv.m2",
      "chat.routeIntro.cv.m3",
      "chat.routeIntro.cv.m4",
      "chat.routeIntro.cv.m5",
    ],
    chips: [
      {
        id: "cv.rewriteSummary",
        titleKey: "chat.routeIntro.cv.chips.rewriteSummary",
        prompt: "Rewrite my CV summary around my strongest results",
      },
      {
        id: "cv.strongerBullets",
        titleKey: "chat.routeIntro.cv.chips.strongerBullets",
        prompt: "Make my experience bullets specific and measurable",
      },
      {
        id: "cv.gaps",
        titleKey: "chat.routeIntro.cv.chips.gaps",
        prompt: "Review my CV and tell me what is missing or weak",
      },
      {
        id: "cv.tailor",
        titleKey: "chat.routeIntro.cv.chips.tailor",
        prompt: "Tailor my CV for a specific job posting",
      },
    ],
  },

  "cv-tailored": {
    messageKeys: [
      "chat.routeIntro.cvTailored.m1",
      "chat.routeIntro.cvTailored.m2",
      "chat.routeIntro.cvTailored.m3",
      "chat.routeIntro.cvTailored.m4",
      "chat.routeIntro.cvTailored.m5",
    ],
    chips: [
      {
        id: "cvTailored.matchKeywords",
        titleKey: "chat.routeIntro.cvTailored.chips.matchKeywords",
        prompt: "Match this CV to the keywords in the job description",
      },
      {
        id: "cvTailored.compare",
        titleKey: "chat.routeIntro.cvTailored.chips.compare",
        prompt: "How does this tailored version differ from my main CV?",
      },
      {
        id: "cvTailored.tighten",
        titleKey: "chat.routeIntro.cvTailored.chips.tighten",
        prompt: "Tighten this CV so it fits on one page",
      },
    ],
  },

  jobs: {
    messageKeys: [
      "chat.routeIntro.jobs.m1",
      "chat.routeIntro.jobs.m2",
      "chat.routeIntro.jobs.m3",
      "chat.routeIntro.jobs.m4",
      "chat.routeIntro.jobs.m5",
    ],
    chips: [
      {
        id: "jobs.remoteGcc",
        titleKey: "chat.routeIntro.jobs.chips.remoteGcc",
        prompt: "Find remote roles in the GCC that match my profile",
      },
      {
        id: "jobs.bestMatches",
        titleKey: "chat.routeIntro.jobs.chips.bestMatches",
        prompt: "Which of these jobs fit me best, and why?",
      },
      {
        id: "jobs.recent",
        titleKey: "chat.routeIntro.jobs.chips.recent",
        prompt: "Show only jobs posted in the last week",
      },
      {
        id: "jobs.easyApply",
        titleKey: "chat.routeIntro.jobs.chips.easyApply",
        prompt: "Show jobs I can apply to directly",
      },
    ],
  },

  "job-detail": {
    messageKeys: [
      "chat.routeIntro.jobDetail.m1",
      "chat.routeIntro.jobDetail.m2",
      "chat.routeIntro.jobDetail.m3",
      "chat.routeIntro.jobDetail.m4",
      "chat.routeIntro.jobDetail.m5",
    ],
    chips: [
      {
        id: "jobDetail.fit",
        titleKey: "chat.routeIntro.jobDetail.chips.fit",
        prompt: "How well do I fit this job, and what is missing?",
      },
      {
        id: "jobDetail.tailor",
        titleKey: "chat.routeIntro.jobDetail.chips.tailor",
        prompt: "Tailor my CV for this job",
      },
      {
        id: "jobDetail.apply",
        titleKey: "chat.routeIntro.jobDetail.chips.apply",
        prompt: "Prepare my application for this job",
      },
      {
        id: "jobDetail.prep",
        titleKey: "chat.routeIntro.jobDetail.chips.prep",
        prompt: "Prepare me for an interview for this role",
      },
    ],
  },

  skills: {
    messageKeys: [
      "chat.routeIntro.skills.m1",
      "chat.routeIntro.skills.m2",
      "chat.routeIntro.skills.m3",
      "chat.routeIntro.skills.m4",
      "chat.routeIntro.skills.m5",
    ],
    chips: [
      {
        id: "skills.recommend",
        titleKey: "chat.routeIntro.skills.chips.recommend",
        prompt: "Recommend skills that fit my career goals",
      },
      {
        id: "skills.run",
        titleKey: "chat.routeIntro.skills.chips.run",
        prompt: "Show available CV skills I can run to improve my CV",
      },
      {
        id: "skills.explain",
        titleKey: "chat.routeIntro.skills.chips.explain",
        prompt: "What are skills and how do they help me?",
      },
    ],
  },

  automation: {
    messageKeys: [
      "chat.routeIntro.automation.m1",
      "chat.routeIntro.automation.m2",
      "chat.routeIntro.automation.m3",
      "chat.routeIntro.automation.m4",
      "chat.routeIntro.automation.m5",
    ],
    chips: [
      {
        id: "automation.setUp",
        titleKey: "chat.routeIntro.automation.chips.setUp",
        prompt: "Draft an auto-apply automation for roles that match me",
      },
      {
        id: "automation.review",
        titleKey: "chat.routeIntro.automation.chips.review",
        prompt: "Review my job automation activity and current rules",
      },
      {
        id: "automation.rules",
        titleKey: "chat.routeIntro.automation.chips.rules",
        prompt: "Draft a weekly automation to find roles that match my profile",
      },
    ],
  },

  connectors: {
    messageKeys: [
      "chat.routeIntro.connectors.m1",
      "chat.routeIntro.connectors.m2",
      "chat.routeIntro.connectors.m3",
      "chat.routeIntro.connectors.m4",
      "chat.routeIntro.connectors.m5",
    ],
    chips: [
      {
        id: "connectors.which",
        titleKey: "chat.routeIntro.connectors.chips.which",
        prompt: "Show me which connector would help my job search most",
      },
      {
        id: "connectors.linkedin",
        titleKey: "chat.routeIntro.connectors.chips.linkedin",
        prompt:
          "Show me how to connect Tabbio to ChatGPT, Claude, Gemini, or Grok",
      },
    ],
  },

  memory: {
    messageKeys: [
      "chat.routeIntro.memory.m1",
      "chat.routeIntro.memory.m2",
      "chat.routeIntro.memory.m3",
      "chat.routeIntro.memory.m4",
      "chat.routeIntro.memory.m5",
    ],
    chips: [
      {
        id: "memory.what",
        titleKey: "chat.routeIntro.memory.chips.what",
        prompt: "What do you remember about my career so far?",
      },
      {
        id: "memory.correct",
        titleKey: "chat.routeIntro.memory.chips.correct",
        prompt: "Help me update the target role you remember for me",
      },
    ],
  },

  messages: {
    messageKeys: [
      "chat.routeIntro.messages.m1",
      "chat.routeIntro.messages.m2",
      "chat.routeIntro.messages.m3",
    ],
    chips: [
      {
        id: "messages.unread",
        titleKey: "chat.routeIntro.messages.chips.unread",
        prompt: "Show my unread conversations",
      },
      {
        id: "messages.findPerson",
        titleKey: "chat.routeIntro.messages.chips.findPerson",
        prompt: "Find someone I can message",
      },
      {
        id: "messages.draftReply",
        titleKey: "chat.routeIntro.messages.chips.draftReply",
        prompt: "Help me draft a reply to this conversation",
      },
    ],
  },

  notifications: {
    messageKeys: [
      "chat.routeIntro.notifications.m1",
      "chat.routeIntro.notifications.m2",
      "chat.routeIntro.notifications.m3",
    ],
    chips: [
      {
        id: "notifications.unread",
        titleKey: "chat.routeIntro.notifications.chips.unread",
        prompt: "Show my unread notifications",
      },
      {
        id: "notifications.markAllRead",
        titleKey: "chat.routeIntro.notifications.chips.markAllRead",
        prompt: "Mark all my notifications as read",
      },
      {
        id: "notifications.preferences",
        titleKey: "chat.routeIntro.notifications.chips.preferences",
        prompt: "Review my notification delivery settings",
      },
    ],
  },

  analytics: {
    messageKeys: [
      "chat.routeIntro.analytics.m1",
      "chat.routeIntro.analytics.m2",
      "chat.routeIntro.analytics.m3",
    ],
    chips: [
      {
        id: "analytics.reach",
        titleKey: "chat.routeIntro.analytics.chips.reach",
        prompt: "Show my public CV reach for the last 30 days",
      },
      {
        id: "analytics.sources",
        titleKey: "chat.routeIntro.analytics.chips.sources",
        prompt: "Where are my recent public CV views coming from?",
      },
      {
        id: "analytics.compare",
        titleKey: "chat.routeIntro.analytics.chips.compare",
        prompt: "Compare the performance of my public CVs",
      },
    ],
  },

  "application-studio": {
    messageKeys: [
      "chat.routeIntro.applicationStudio.m1",
      "chat.routeIntro.applicationStudio.m2",
      "chat.routeIntro.applicationStudio.m3",
    ],
    chips: [
      {
        id: "applicationStudio.score",
        titleKey: "chat.routeIntro.applicationStudio.chips.score",
        prompt: "Score my main CV for a job",
      },
      {
        id: "applicationStudio.coverLetter",
        titleKey: "chat.routeIntro.applicationStudio.chips.coverLetter",
        prompt: "Draft a cover letter for a job",
      },
      {
        id: "applicationStudio.interview",
        titleKey: "chat.routeIntro.applicationStudio.chips.interview",
        prompt: "Prepare interview questions for a job",
      },
      {
        id: "applicationStudio.saved",
        titleKey: "chat.routeIntro.applicationStudio.chips.saved",
        prompt: "Show my saved career tool results",
      },
    ],
  },
};

// UI-only collection copy. The transport keeps the existing cv-tailored
// screen key and sends no CV entity while the user is browsing the hub.
const CV_HUB_INTRO: RouteIntro = {
  messageKeys: ["chat.routeIntro.cvHub.m1", "chat.routeIntro.cvHub.m2", "chat.routeIntro.cvHub.m3"],
  chips: [
    { id: "cvHub.create", titleKey: "chat.routeIntro.cvHub.chips.create", prompt: "Help me create a new CV. Ask which source I want to start from." },
    { id: "cvHub.choose", titleKey: "chat.routeIntro.cvHub.chips.choose", prompt: "Help me choose which of my CVs fits a role. Ask me about the role first." },
    { id: "cvHub.manage", titleKey: "chat.routeIntro.cvHub.chips.manage", prompt: "Show my CVs and help me decide which one to work on." },
  ],
};

function isCvHubRoute(pathname?: string | null) {
  const path = normalizeRoutePath(pathname);
  return path === "/cv/my-tabbio" || path === "/cv/tailored";
}

export function getRouteIntro(
  screen: PageContextScreen,
  pathname?: string | null,
): RouteIntro | undefined {
  if (screen === "cv-tailored" && isCvHubRoute(pathname)) return CV_HUB_INTRO;
  return ROUTE_INTROS[screen];
}

/**
 * The screen tag shown inside the composer, so it is always visible which
 * page the agent will act on when you hit send. Label key + Material Symbol
 * per screen; screens absent here render no tag.
 */
const SCREEN_TAGS: Partial<
  Record<PageContextScreen, { labelKey: string; icon: string }>
> = {
  cv: { labelKey: "chat.pageTag.cv", icon: "description" },
  "cv-tailored": { labelKey: "chat.pageTag.cvTailored", icon: "edit_document" },
  jobs: { labelKey: "chat.pageTag.jobs", icon: "work" },
  "job-detail": { labelKey: "chat.pageTag.jobDetail", icon: "work" },
  skills: { labelKey: "chat.pageTag.skills", icon: "bolt" },
  automation: { labelKey: "chat.pageTag.automation", icon: "conversion_path" },
  connectors: { labelKey: "chat.pageTag.connectors", icon: "power" },
  memory: { labelKey: "chat.pageTag.memory", icon: "graph_1" },
  messages: { labelKey: "chat.pageTag.messages", icon: "forum" },
  notifications: {
    labelKey: "chat.pageTag.notifications",
    icon: "notifications",
  },
  analytics: { labelKey: "chat.pageTag.analytics", icon: "analytics" },
  "application-studio": {
    labelKey: "chat.pageTag.applicationStudio",
    icon: "workspaces",
  },
};

export function getScreenTag(screen: PageContextScreen, pathname?: string | null) {
  if (screen === "cv-tailored" && isCvHubRoute(pathname)) {
    return { labelKey: "chat.pageTag.cvHub", icon: "folder_open" };
  }
  return SCREEN_TAGS[screen];
}

const RECENT_KEY_PREFIX = "tabbio_route_intro_recent.";

/**
 * Picks a variant index, avoiding the ones shown most recently on this screen
 * so repeat visits read differently. Keeps a short ring of recent indexes in
 * localStorage; falls back to a plain rotation when storage is unavailable
 * (Safari private mode) and to index 0 when everything has been seen.
 */
export function pickIntroVariantIndex(
  screen: string,
  variantCount: number,
): number {
  if (variantCount <= 1) return 0;

  const key = `${RECENT_KEY_PREFIX}${screen}`;
  // Remember at most half the catalog, so there is always something to pick.
  const historySize = Math.max(1, Math.floor(variantCount / 2));

  let recent: number[] = [];
  try {
    const raw = readWebStorage(key);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      recent = parsed.filter(
        (n): n is number => typeof n === "number" && n >= 0 && n < variantCount,
      );
    }
  } catch {
    recent = [];
  }

  const candidates: number[] = [];
  for (let i = 0; i < variantCount; i += 1) {
    if (!recent.includes(i)) candidates.push(i);
  }

  const pool =
    candidates.length > 0 ? candidates : [...Array(variantCount).keys()];
  const picked = pool[Math.floor(Math.random() * pool.length)] ?? 0;

  writeWebStorage(
    key,
    JSON.stringify([picked, ...recent].slice(0, historySize)),
  );
  return picked;
}
